/**
 * A folder's page head (the items list with `folder=`): where it is (a breadcrumb from the space's items), its name,
 * who sees it and its id; making things in it, sharing it (what's inside asked about), and in its "⋯" renaming,
 * moving, archiving and deleting it. Then its tabs: its items, its conversation.
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { Conversation, FolderOut, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { Action } from "../../lib/actions.ts";
import { folderHref } from "../../lib/folders.ts";
import { sayDone } from "../../lib/jobs.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { asApiError, bump, type Resource, useAction } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { location, navigate } from "../../lib/router.ts";
import { toast } from "../../lib/toast.ts";
import { useId } from "../../lib/uid.ts";
import { ArchivedIcon, FolderIcon, TrashIcon, UnarchiveIcon } from "../../ui/icons.tsx";
import { Badge } from "../../ui/misc.tsx";
import { PageHeader } from "../../ui/PageHeader.tsx";
import { buttonClass } from "../../ui/styles.ts";
import { TabNav } from "../../ui/TabNav.tsx";
import { ConversationLabel } from "../comments/conversations.tsx";
import { ShareButton, SharedIcon, VisibilityMark } from "../item/visibility.tsx";
import { Breadcrumb } from "./breadcrumb.tsx";
import { MoveDialog } from "./move.tsx";
import { ArchiveFolderDialog, DeleteFolderDialog } from "./remove.tsx";
import { useFolderSharing } from "./sharing.tsx";

const NAME_TAKEN = "name_taken";

type Dialog = "move" | "archive" | "delete";

/** The folder's name renamed (a name taken beside it said, nothing changed). */
function useRenameFolder(space: SpaceOut, r: Resource<FolderOut | null>, folder: FolderOut) {
  const [rename] = useAction(async (name: string) => {
    try {
      r.set(await api.patch<FolderOut>(spaceApi(space, `/folders/${folder.id}`), { name, ver: folder.ver }));
      bump();
      toast(t("folders.renamed"));
    } catch (e) {
      if (asApiError(e).code === NAME_TAKEN) {
        toast(t("folders.taken", { name }), "error");
        return;
      }
      throw e;
    }
  });
  return rename;
}

/** The folder's dialogs: moving, archiving (or bringing back) and deleting it. */
function FolderDialogs({
  space,
  r,
  folder,
  dialog,
  onClose,
}: {
  space: SpaceOut;
  r: Resource<FolderOut | null>;
  folder: FolderOut;
  dialog: Dialog | null;
  onClose: () => void;
}) {
  return (
    <>
      <MoveDialog
        space={space}
        open={dialog === "move"}
        title={t("folders.moveTitle", { name: folder.name })}
        moving={{ kind: "folder", folder }}
        onClose={onClose}
        onDone={(m) => {
          if ("job" in m) {
            sayDone(m.job);
          }
          r.reload();
        }}
      />
      <ArchiveFolderDialog
        space={space}
        open={dialog === "archive"}
        folder={folder}
        onClose={onClose}
        onDone={(f) => (f ? r.set(f) : r.reload())}
      />
      <DeleteFolderDialog
        space={space}
        open={dialog === "delete"}
        folder={folder}
        onClose={onClose}
        onDone={() => {
          bump();
          navigate(folderHref(space, folder.under ?? null, location.value.searchParams));
        }}
      />
    </>
  );
}

/**
 * The folder's head and tabs: where it is, its name (renamed in place), who sees it and its id; `buttons` (what's
 * made from it: "New"), uploading into it, sharing it (what's inside asked about), and in its "⋯" moving, archiving
 * and deleting it.
 */
export function FolderHeader(p: {
  space: SpaceOut;
  r: Resource<FolderOut | null>;
  folder: FolderOut;
  conv: Resource<Conversation>;
  tab: "items" | "conversation";
  buttons?: readonly Action[];
}) {
  const { space, folder, r } = p;
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const rename = useRenameFolder(space, r, folder);
  const sharing = useFolderSharing(space, folder, (f) => (f ? r.set(f) : r.reload()), true);
  const panel = useId();
  const sp = location.value.searchParams;
  const base = folderHref(space, folder.id, sp);
  const editable = canEdit(space);
  const buttons: Action[] = editable
    ? [
        ...(p.buttons ?? []),
        {
          key: "share",
          label: t("visibility.share"),
          icon: <SharedIcon item={folder} />,
          symbol: "person.2",
          onSelect: () => document.getElementById(panel)?.showPopover(),
          node: (
            <ShareButton
              space={space}
              item={folder}
              members={undefined}
              onDone={() => undefined}
              send={sharing.change}
              link={`${globalThis.location.origin}${folderHref(space, folder.id)}`}
              class={buttonClass("secondary")}
              panel={panel}
            />
          ),
        },
        {
          key: "move",
          label: t("folders.moveTo"),
          icon: <FolderIcon />,
          overflow: true,
          onSelect: () => setDialog("move"),
        },
        {
          key: "archive",
          label: t(folder.archived ? "item.unarchive" : "item.archive"),
          icon: folder.archived ? <UnarchiveIcon /> : <ArchivedIcon />,
          overflow: true,
          onSelect: () => setDialog("archive"),
        },
        {
          key: "delete",
          label: t("common.delete"),
          icon: <TrashIcon />,
          danger: true,
          overflow: true,
          separated: true,
          onSelect: () => setDialog("delete"),
        },
      ]
    : [];
  return (
    <>
      <PageHeader
        above={<Breadcrumb space={space} path={folder.path} root={true} drop={editable} class="mb-1" />}
        title={folder.name}
        icon={<FolderIcon size={22} class="shrink-0 text-zinc-500" />}
        id={folder.id}
        marks={
          <>
            <VisibilityMark item={folder} members={undefined} size={18} />
            {folder.archived ? <Badge tone="gray">{t("item.archivedBadge")}</Badge> : null}
          </>
        }
        rename={
          editable
            ? {
                editing,
                onEdit: setEditing,
                onRename: (name) => rename(name),
                value: folder.name,
                label: t("folders.rename"),
                max: 255,
              }
            : undefined
        }
        buttons={buttons}
        more={t("folders.more")}
      />
      {sharing.dialogs}
      <FolderDialogs space={space} r={r} folder={folder} dialog={dialog} onClose={() => setDialog(null)} />
      <TabNav
        label={t("folders.sections")}
        tabs={[
          { href: base, label: t("items.tab.items"), current: p.tab === "items" },
          {
            href: spaceUrl(space, `/items?folder=${folder.id}&tab=conversation`),
            label: <ConversationLabel conv={p.conv} />,
            current: p.tab === "conversation",
          },
        ]}
      />
    </>
  );
}
